import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { emit } from '../utils/dom.utils.js';
import type { Size } from '../types.js';
import styles from './styles.js';

/**
 * uwc-chip
 *
 * A compact inline element for labels, tags, contacts, or filter tokens.
 * Supports an avatar image, a leading icon, a removable close button, and a
 * fully slotted content area.
 *
 * @attr {string}  label       - Chip text. Overridden by the default slot.
 * @attr {string}  icon        - Bootstrap Icon name shown before the label.
 * @attr {string}  image       - URL for a circular avatar shown at the leading edge.
 * @attr {string}  alt         - Alt text for the avatar image.
 * @attr {boolean} removable   - Show a close button that fires uwc-remove on click.
 * @attr {string}  remove-icon - Icon name for the close button. Default: "x-lg".
 * @attr {'small'|'medium'|'large'} size - Chip scale (em-based height follows font). Default: medium.
 *
 * @slot (default)    - Custom chip content; overrides the `label` attribute.
 * @slot removeicon   - Custom remove icon; overrides the `remove-icon` attribute.
 *
 * @fires {CustomEvent} uwc-remove - Fired when the remove button is clicked.
 *
 * @cssprop --uwc-chip-bg           - Chip background. Default: surface-raised.
 * @cssprop --uwc-chip-border       - Chip border. Default: 1px solid rgba(0,0,0,.09).
 * @cssprop --uwc-chip-color        - Chip text color. Default: --uwc-text.
 * @cssprop --uwc-chip-radius       - Border radius. Default: 9999px (pill).
 * @cssprop --uwc-chip-font-size    - Font size. Default: 0.875rem.
 * @cssprop --uwc-chip-font-weight  - Font weight. Default: 500.
 * @cssprop --uwc-chip-icon-color   - Leading icon color. Default: --uwc-text-secondary.
 * @cssprop --uwc-chip-remove-color - Remove icon color. Default: --uwc-text-secondary.
 */
export class UwcChip extends LitElement {

  static override styles: CSSResultGroup = styles;

  @property({ reflect: true }) label       = '';
  @property({ reflect: true }) icon        = '';
  @property({ reflect: true }) image       = '';
  @property({ reflect: true }) alt         = '';
  @property({ reflect: true, attribute: 'remove-icon' }) removeIcon = 'x-lg';
  @property({ type: Boolean, reflect: true }) removable = false;
  @property({ reflect: true }) size: Size = 'medium';

  // ── Handlers ─────────────────────────────────────────────────────────────

  private _onRemove(e: MouseEvent) {
    e.stopPropagation();
    emit(this, 'uwc-remove');
  }

  // ── Sub-templates ─────────────────────────────────────────────────────────

  private _renderLeading() {
    if (this.image) {
      return html`
        <img
          class="uwc-chip__image"
          part="image"
          src="${this.image}"
          alt="${this.alt}"
          loading="lazy"
          decoding="async"
        />
      `;
    }
    if (this.icon) {
      return html`
        <uwc-icon
          name="${this.icon}"
          size="1rem"
          class="uwc-chip__icon"
          part="icon"
          aria-hidden="true"
        ></uwc-icon>
      `;
    }
    return nothing;
  }

  private _renderRemove() {
    if (!this.removable) return nothing;
    return html`
      <button
        class="uwc-chip__remove"
        part="remove-button"
        type="button"
        aria-label="Remove"
        @click="${this._onRemove}"
      >
        <slot name="removeicon">
          <uwc-icon
            name="${this.removeIcon}"
            size="0.7rem"
            aria-hidden="true"
          ></uwc-icon>
        </slot>
      </button>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    return html`
      <div class="uwc-chip" part="base">
        ${this._renderLeading()}
        <span class="uwc-chip__label" part="label">
          <slot>${this.label}</slot>
        </span>
        ${this._renderRemove()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-chip': UwcChip; }
}
